import { makeAutoObservable, action, runInAction, observable } from 'mobx';
import * as ExcelJS from 'exceljs';
import { FileSystemService } from '../services/FileSystemService';
import { openDB } from 'idb';

export class FileSystemStore {
    rootStore = null;
    fileHandles = [];
    currentFileHandle = null;
    directoryHandle = null;
    isSaved = true;
    fileSystemService = null;
    
    // New properties for directory browsing
    currentDirectoryContents = [];
    isLoadingDirectory = false;
    currentPath = [];
    recentDirectories = []; // New property to store recent directories

    // Add this property to the class
    directoryTree = null;

    // Add a property to track the selected file
    selectedFilePath = null;

    // Add a property to track the recently accessed directory
    recentlyAccessedDirectory = null;

    // Add this new property to track expanded paths
    expandedPaths = new Set();

    constructor(rootStore) {
        this.rootStore = rootStore;
        
        // Initialize the fileSystemService if needed
        if (!this.fileSystemService) {
            this.fileSystemService = new FileSystemService();
        }
        
        // Bind methods
        this.toggleExpandNode = this.toggleExpandNode.bind(this);
        
        makeAutoObservable(this, {
            rootStore: false,
            fileHandles: true,
            currentFileHandle: true,
            directoryHandle: true,
            fileSystemService: false,
            currentDirectoryContents: observable,
            isLoadingDirectory: observable,
            currentPath: observable,
            recentDirectories: observable,
            directoryTree: observable,
            selectedFilePath: observable,
            recentlyAccessedDirectory: observable,
            expandedPaths: observable
        });
        
        // Initialize the database and load recent directories
        this.initializeDB();
        
        // Initial empty tree structure
        this.directoryTree = observable({
            kind: 'directory',
            name: '',
            isExpanded: true,
            isLoading: false,
            children: [],
            handle: null
        });
    }

    // Initialize the IndexedDB database
    initializeDB = async () => {
        try {
            console.log('Initializing IndexedDB for directory handles');
            const db = await openDB('editera-storage', 1, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains('directory-handles')) {
                        db.createObjectStore('directory-handles', { keyPath: 'id' });
                    }
                }
            });
            console.log('IndexedDB initialized successfully');
            
            // Load recent directories
            await this.loadRecentDirectories();
            
            // Auto-restore the most recent directory if available
            if (this.recentDirectories.length > 0) {
                console.log('Attempting to auto-restore most recent directory');
                this.attemptAutoRestore();
            }
        } catch (error) {
            console.error('Error initializing IndexedDB:', error);
        }
    };

    // Load recent directories from IndexedDB
    loadRecentDirectories = async () => {
        try {
            console.log('Loading recent directories from IndexedDB');
            const db = await openDB('editera-storage', 1);
            const allDirHandles = await db.getAll('directory-handles');
            
            console.log(`Found ${allDirHandles.length} directory handles in IndexedDB`);
            
            // Sort by lastAccessed (most recent first)
            allDirHandles.sort((a, b) => b.lastAccessed - a.lastAccessed);
            
            runInAction(() => {
                this.recentDirectories = allDirHandles;
            });
            
            // Try to restore access to the most recently used directory
            if (allDirHandles.length > 0) {
                this.tryRestoreDirectoryAccess(allDirHandles[0].handle);
            }
        } catch (error) {
            console.error('Error loading recent directories:', error);
        }
    };

    // Store a directory handle in IndexedDB
    storeDirectoryHandle = async (dirHandle) => {
        try {
            console.log('Storing directory handle in IndexedDB:', dirHandle.name);
            
            // Create a unique ID for this directory
            const dirId = `dir-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            
            const db = await openDB('editera-storage', 1);
            
            // Check if we already have this directory stored
            const allDirHandles = await db.getAll('directory-handles');
            const existingDir = allDirHandles.find(dir => dir.name === dirHandle.name);
            
            if (existingDir) {
                console.log('Directory already exists in IndexedDB, updating lastAccessed');
                await db.put('directory-handles', {
                    ...existingDir,
                    handle: dirHandle,
                    lastAccessed: Date.now()
                });
            } else {
                console.log('Adding new directory to IndexedDB');
                await db.add('directory-handles', {
                    id: dirId,
                    name: dirHandle.name,
                    handle: dirHandle,
                    lastAccessed: Date.now()
                });
            }
            
            console.log('Successfully stored directory handle');
            
            // Reload the recent directories list
            this.loadRecentDirectories();
        } catch (error) {
            console.error('Error storing directory handle:', error);
        }
    };

    // Try to restore access to a directory handle
    tryRestoreDirectoryAccess = async (dirHandle) => {
        if (!dirHandle) {
            console.log('No directory handle provided to restore');
            return false;
        }
        
        try {
            console.log('Attempting to restore access to directory:', dirHandle.name);
            
            const permissionState = await dirHandle.requestPermission({ mode: 'readwrite' });
            console.log('Permission request result:', permissionState);
            
            if (permissionState === 'granted') {
                console.log('Permission granted, setting up directory access');
                
                // Update the lastAccessed timestamp
                const db = await openDB('editera-storage', 1);
                const allDirHandles = await db.getAll('directory-handles');
                const existingDir = allDirHandles.find(dir => 
                    dir.handle && dir.name === dirHandle.name);
                
                if (existingDir) {
                    await db.put('directory-handles', {
                        ...existingDir,
                        lastAccessed: Date.now()
                    });
                }
                
                runInAction(() => {
                    this.directoryHandle = dirHandle;
                    this.currentPath = [dirHandle.name];
                });
                
                // Build the directory tree
                await this.buildDirectoryTree(dirHandle);
                return true;
            } else {
                console.log('Permission not granted for directory');
                return false;
            }
        } catch (error) {
            console.error('Error restoring directory access:', error);
            return false;
        }
    };

    // Request access to a new directory
    requestDirectoryAccess = async () => {
        if (!this.isFileSystemAccessSupported()) {
            console.warn('File System Access API not supported');
            return null;
        }

        try {
            console.log('Requesting directory access...');
            const dirHandle = await window.showDirectoryPicker({
                mode: 'readwrite'
            });
            
            console.log('Directory access granted for:', dirHandle.name);
            
            // Store the directory handle in IndexedDB for persistence
            await this.storeDirectoryHandle(dirHandle);
            
            runInAction(() => {
                this.directoryHandle = dirHandle;
                this.currentPath = [dirHandle.name];
            });
            
            // Build the directory tree
            await this.buildDirectoryTree(dirHandle);
            
            return dirHandle;
        } catch (error) {
            console.error('Error requesting directory access:', error);
            return null;
        }
    };

    // Load contents of a directory
    loadDirectoryContents = async (dirHandle = this.directoryHandle) => {
        if (!dirHandle) {
            console.warn('No directory handle available');
            return [];
        }
        
        console.log('Loading contents of directory:', dirHandle.name);
        
        runInAction(() => {
            this.isLoadingDirectory = true;
        });
        
        try {
            // Build the directory tree
            await this.buildDirectoryTree(dirHandle);
            
            // Keep currentDirectoryContents for backward compatibility
            const contents = this.directoryTree.children.map(child => ({
                name: child.name,
                kind: child.kind,
                handle: child.handle,
                path: child.path
            }));
            
            runInAction(() => {
                this.currentDirectoryContents = contents;
                this.isLoadingDirectory = false;
            });
            
            return contents;
        } catch (error) {
            console.error('Error loading directory contents:', error);
            runInAction(() => {
                this.isLoadingDirectory = false;
                this.currentDirectoryContents = [];
            });
            return [];
        }
    };

    // Navigate to a subdirectory
    navigateToDirectory = async (entry) => {
        if (entry.kind !== 'directory' || !entry.handle) {
            console.warn('Not a valid directory entry');
            return;
        }
        
        console.log('Navigating to directory:', entry.name);
        
        runInAction(() => {
            this.directoryHandle = entry.handle;
            this.currentPath = entry.path;
            this.isLoadingDirectory = true;
        });
        
        await this.loadDirectoryContents(entry.handle);
    };

    // Navigate up one level
    navigateUp = async () => {
        if (this.currentPath.length <= 1 || !this.directoryHandle) {
            console.warn('Already at root level');
            return;
        }
        
        try {
            console.log('Navigating up from current directory');
            
            // Get the parent directory handle
            const parentHandle = await this.directoryHandle.getParent();
            if (!parentHandle) {
                console.warn('No parent directory found');
                return;
            }
            
            runInAction(() => {
                this.directoryHandle = parentHandle;
                this.currentPath = this.currentPath.slice(0, -1);
                this.isLoadingDirectory = true;
            });
            
            await this.loadDirectoryContents(parentHandle);
        } catch (error) {
            console.error('Error navigating up:', error);
        }
    };

    // Enhanced method to check if File System Access API is fully supported
    isFileSystemAccessSupported() {
        // Basic check for the File System Access API
        const hasBasicSupport = 'showOpenFilePicker' in window && 'showDirectoryPicker' in window;
        
        // Check for IndexedDB support (needed for persistence)
        const hasIndexedDBSupport = 'indexedDB' in window;
        
        console.log('File System Access API support check:', {
            hasBasicSupport,
            hasIndexedDBSupport
        });
        
        return hasBasicSupport && hasIndexedDBSupport;
    }

    // Check if the browser supports persistent permissions
    supportsPersistentPermissions() {
        const userAgent = navigator.userAgent;
        
        // Check for Chrome 122+
        const chromeMatch = userAgent.match(/Chrome\/(\d+)/);
        
        // Check for Edge (which is Chromium-based now)
        const isEdge = userAgent.indexOf("Edg") > -1;
        const edgeMatch = isEdge ? userAgent.match(/Edg\/(\d+)/) : null;
        
        if (chromeMatch) {
            const chromeVersion = parseInt(chromeMatch[1], 10);
            // Chrome 122+ supports persistent permissions
            const hasPersistentSupport = chromeVersion >= 122;
            
            console.log(`Chrome version detected: ${chromeVersion}, supports persistent permissions: ${hasPersistentSupport}`);
            return hasPersistentSupport;
        } else if (edgeMatch) {
            const edgeVersion = parseInt(edgeMatch[1], 10);
            // Edge follows Chrome's versioning closely, so we'll use the same threshold
            const hasPersistentSupport = edgeVersion >= 122;
            
            console.log(`Edge version detected: ${edgeVersion}, supports persistent permissions: ${hasPersistentSupport}`);
            return hasPersistentSupport;
        }
        
        // For other browsers, assume no support for now
        console.log('Browser not detected as Chrome or Edge, assuming no persistent permissions support');
        return false;
    }

    // Verify if we still have access to the directory
    verifyDirectoryAccess = async () => {
        if (!this.directoryHandle) {
            console.log('No directory handle to verify');
            return false;
        }
        
        try {
            console.log('Verifying access to directory:', this.directoryHandle.name);
            // Try to read from the directory to verify we still have access
            await this.directoryHandle.getDirectoryHandle('.', { create: false });
            console.log('Successfully verified directory access');
            return true;
        } catch (error) {
            console.log('No longer have access to directory:', error);
            return false;
        }
    };

    // Remove a directory from recent directories
    removeRecentDirectory = async (dirId) => {
        try {
            console.log('Removing directory from recent list:', dirId);
            const db = await openDB('editera-storage', 1);
            await db.delete('directory-handles', dirId);
            
            // Reload the recent directories list
            this.loadRecentDirectories();
        } catch (error) {
            console.error('Error removing recent directory:', error);
        }
    };

    
   // Add this action to clear the selection
    clearSelectedFile = action(() => {
        console.log("Clearing selected file path:", this.selectedFilePath);
        // Always set to null without checking, preventing race conditions
        this.selectedFilePath = null;
    });

    openFileFromDirectory = async (fileEntry) => {
        if (!fileEntry || fileEntry.kind !== 'file' || !fileEntry.handle) {
            return null;
        }
        
        try {
            const hasPermission = await this.verifyPermission(fileEntry.handle, false);
            if (!hasPermission) {
                throw new Error('Read permission denied');
            }
            
            const file = await fileEntry.handle.getFile();
            
            // Mark this file as selected *after* successfully getting it
            runInAction(() => {
                this.selectedFilePath = fileEntry.path.join('/');
                console.log("Selected file path set to:", this.selectedFilePath);
            });
            
            // Add to recent files in settings store with directory info if we have permission
            const hasUserDataPermission = localStorage.getItem('userDataPermissionGranted') === 'true';
            
            if (this.rootStore.settingsStore && hasUserDataPermission) {
                this.rootStore.settingsStore.addRecentFile({
                    name: file.name,
                    directoryPath: this.directoryHandle?.name,
                    relativePath: fileEntry.path.slice(1).join('/'), // Remove root dir from path
                    size: file.size,
                    lastModified: file.lastModified,
                    lastOpened: new Date().toISOString()
                });
            }
            
            return { file, handle: fileEntry.handle };
        } catch (error) {
            console.error('Error opening file from directory:', error);
            // Clear selection if opening failed
            runInAction(() => {
                this.selectedFilePath = null;
            });
            return null;
        }
    };

    openFile = async () => {
        const result = await this.fileSystemService.openFile();
        
        // Add to recent files if we have a handle
        if (result.handle && this.rootStore.settingsStore) {
            this.rootStore.settingsStore.addRecentFile({
                name: result.file.name,
                path: result.handle.name || `Unknown/${result.file.name}`,
                size: result.file.size,
                lastModified: result.file.lastModified,
                lastOpened: new Date().toISOString()
                // Don't store the handle in settings
            });
        }
        
        return result;
    };

    saveWorkbook = async (workbook, suggestedFileName = 'workbook.xlsx') => {
        console.log('Saving workbook:', {
            isNew: workbook.isNewWorkbook,
            suggestedFileName,
            currentHandle: this.currentFileHandle?.name,
            targetDirectoryHandle: workbook.targetDirectoryHandle?.name,
        });
        let handle = this.currentFileHandle;
        let fileName = suggestedFileName;
        let success = false;
        let saveError = null;
        let usedTargetDirectory = false;
        let attemptedSavePath = 'Unknown';

        try {
            // --- Determine Save Path and Get Handle ---
            // Path 1: New file created via '+' button (has targetDirectoryHandle)
            if (workbook.isNewWorkbook && workbook.targetDirectoryHandle) {
                attemptedSavePath = 'Path 1 (New file in Target Dir)';
                console.log(`[${attemptedSavePath}] Target:`, workbook.targetDirectoryHandle.name);
                try {
                    console.log(`Attempting getFileHandle for "${suggestedFileName}" in target directory.`);
                    handle = await workbook.targetDirectoryHandle.getFileHandle(suggestedFileName, { create: true });
                    fileName = handle.name;
                    usedTargetDirectory = true;
                    console.log(`[${attemptedSavePath}] Success. Got handle:`, fileName);
                } catch (dirError) {
                    console.error(`[${attemptedSavePath}] FAILED getFileHandle in target directory. Error: ${dirError.name} - ${dirError.message}. Falling back.`);
                    handle = null;
                }
            }

            // Path 2: New file created via global 'New File' (no target dir OR Path 1 failed)
            if (workbook.isNewWorkbook && !handle) {
                attemptedSavePath = 'Path 2 (New file via Picker)';
                console.log(`[${attemptedSavePath}] Showing save file picker...`);
                try {
                    handle = await window.showSaveFilePicker({
                        suggestedName: suggestedFileName,
                        types: [{
                            description: 'Excel Files',
                            accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                        }]
                    });
                    fileName = handle.name;
                    console.log(`[${attemptedSavePath}] Picker successful, got handle:`, fileName);
                } catch (pickerError) {
                    if (pickerError.name === 'AbortError') {
                        console.log(`[${attemptedSavePath}] User cancelled save dialog.`);
                        return { success: false, error: 'cancelled' };
                    }
                    throw pickerError;
                }
            }
            // Path 3: Saving an existing file (has currentFileHandle, not new)
            else if (!workbook.isNewWorkbook && this.currentFileHandle) {
                 handle = this.currentFileHandle;
                 fileName = handle.name;
                 attemptedSavePath = 'Path 3 (Existing file save)';
                 console.log(`[${attemptedSavePath}] Using existing handle:`, fileName);
            }
            // Path 4: Saving an existing file but handle was lost
            else if (!workbook.isNewWorkbook && !this.currentFileHandle) {
                 attemptedSavePath = 'Path 4 (Existing file, missing handle)';
                 console.warn(`[${attemptedSavePath}] Prompting Save As via picker...`);
                 try {
                     handle = await window.showSaveFilePicker({
                        suggestedName: suggestedFileName,
                        types: [{
                            description: 'Excel Files',
                            accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
                        }]
                    });
                    fileName = handle.name;
                     console.log(`[${attemptedSavePath}] Picker successful:`, fileName);
                 } catch (pickerError) {
                     if (pickerError.name === 'AbortError') {
                        console.log(`[${attemptedSavePath}] User cancelled save dialog.`);
                        return { success: false, error: 'cancelled' };
                     }
                    throw pickerError;
                 }
             }

            // --- Verify handle exists ---
            if (!handle) {
                 console.error(`Save failed: No valid file handle obtained. Attempted Path: ${attemptedSavePath}`);
                throw new Error("Failed to obtain a valid file handle for saving.");
            }

            // --- Verify Permission and Write ---
            console.log(`[${attemptedSavePath}] Verifying write permission for:`, fileName);
            const hasPermission = await this.verifyPermission(handle, true);
            if (!hasPermission) { throw new Error('Write permission denied'); }

            console.log(`[${attemptedSavePath}] Creating writable stream...`);
            const writable = await handle.createWritable();
            console.log(`[${attemptedSavePath}] Writing buffer...`);

            // *** NEW: Apply UI colors to worksheet tabColor properties before writing ***
            console.log("Applying UI colors to worksheet properties before save...");
            workbook.worksheets.forEach(worksheet => {
                // Skip metadata sheet if it somehow exists
                if (worksheet.name === '_React2Excel_META_x7z9') return; 

                if (worksheet.color && /^#[0-9A-F]{6}$/i.test(worksheet.color)) {
                    const argbColor = 'FF' + worksheet.color.replace('#', '').toUpperCase();
                    worksheet.properties.tabColor = { argb: argbColor };
                    console.log(`  - Applied color ${worksheet.color} (ARGB: ${argbColor}) to sheet ${worksheet.name}`);
                } else {
                    // Ensure tabColor is removed if our custom color is missing or invalid
                    worksheet.properties.tabColor = undefined;
                     if (worksheet.hasOwnProperty('color')) {
                         console.log(`  - Removed/invalid color for sheet ${worksheet.name}, clearing tabColor.`);
                     }
                }
            });
            console.log("Finished applying UI colors.");

            const buffer = await workbook.xlsx.writeBuffer();
            await writable.write(buffer);
            console.log(`[${attemptedSavePath}] Closing stream...`);
            await writable.close();
            console.log(`[${attemptedSavePath}] File write successful.`);

            // --- Post-Success Updates ---
            success = true;

            if (usedTargetDirectory && workbook.targetDirectoryHandle) {
                console.log(`[${attemptedSavePath}] Clearing targetDirectoryHandle.`);
                runInAction(() => { workbook.targetDirectoryHandle = null; });
            }

            // --- Update State (common to all successful saves) ---
            runInAction(() => {
                this.isSaved = true;
                this.currentFileHandle = handle;
                if (this.fileHandles[fileName] !== handle) {
                    this.fileHandles[fileName] = handle;
                }

                // Add to recent files
                if (this.rootStore.settingsStore) {
                    let isInsideCurrentDir = false;
                    let relativePath = undefined;
                     if (this.directoryHandle) {
                         // Basic check - refine if needed
                         if (usedTargetDirectory && workbook.targetDirectoryHandle?.name === this.directoryHandle.name) {
                              isInsideCurrentDir = true;
                              relativePath = fileName;
                         }
                     }
                    const fileInfo = {
                        name: fileName,
                        directoryPath: isInsideCurrentDir ? this.directoryHandle.name : undefined,
                        relativePath: isInsideCurrentDir ? relativePath : undefined,
                        lastOpened: new Date().toISOString(),
                    };
                    this.rootStore.settingsStore.addRecentFile(fileInfo);
                }
            });

            // --- Refresh Directory Tree ---
            // Refresh directory tree if a directory is open.
            // Let refreshDirectoryTree handle restoring the selection state.
            if (this.directoryHandle) {
                 console.log(`[${attemptedSavePath}] Refreshing directory tree after save...`);
                 await this.refreshDirectoryTree();
                 console.log(`[${attemptedSavePath}] Directory tree refreshed. Selection state handled by refreshDirectoryTree.`);
                 // --- NO EXPLICIT RESELECTION HERE ---
            }

        } catch (error) {
            console.error(`Error saving workbook (Attempted ${attemptedSavePath}):`, error);
            saveError = error;
            success = false;
        }

        return { success, fileName: success ? fileName : null, handle: success ? handle : null, error: saveError?.message };
    };

    verifyPermission = async (fileHandle, readWrite) => {
        if (!fileHandle || typeof fileHandle.requestPermission !== 'function') {
            console.warn('Invalid file handle or requestPermission not available');
            return false;
        }
        
        const options = readWrite ? { mode: 'readwrite' } : {};
        
        try {
            const permission = await fileHandle.requestPermission(options);
            return permission === 'granted';
        } catch (error) {
            console.error('Permission denied:', error);
            return false;
        }
    };

    markUnsaved = action((value = false) => {
        this.isSaved = value;
    });

    clearCurrentFileHandle = action(() => {
        this.currentFileHandle = null;
        console.log("Cleared current file handle.");
    });

    // saveWorkbookAs should ALWAYS show the picker
    saveWorkbookAs = async (workbook, suggestedFileName = 'workbook.xlsx') => {
        console.log('Save Workbook As called with suggested name:', suggestedFileName);
        let handle;
        let fileName = suggestedFileName;
        let success = false;
        let saveError = null;

        try {
            // --- Show Save Picker ---
            console.log('Showing save file picker for Save As...');
            try {
                handle = await window.showSaveFilePicker({
                    suggestedName: suggestedFileName,
                    types: [{
                        description: 'Excel Files',
                        accept: {
                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
                        }
                    }]
                });
                 // Update fileName to what the user actually chose
                 fileName = handle.name;
                 console.log('Save picker successful, got handle:', fileName);
            } catch (pickerError) {
                if (pickerError.name === 'AbortError') {
                    console.log('User cancelled the Save As dialog.');
                    return { success: false, error: 'cancelled' };
                }
                 throw pickerError; // Rethrow other picker errors
            }


            // --- Verify Permission and Write ---
             console.log('Verifying write permission for:', fileName);
             const hasPermission = await this.verifyPermission(handle, true);
            if (!hasPermission) {
                throw new Error('Write permission denied');
            }

             console.log('Creating writable stream for:', fileName);
             const writable = await handle.createWritable();
             console.log('Writing buffer to stream...');
             const buffer = await workbook.xlsx.writeBuffer();
            await writable.write(buffer);
             console.log('Closing writable stream...');
            await writable.close();
             console.log('File write successful for:', fileName);

            // --- Update State (Crucially, update currentFileHandle) ---
            runInAction(() => {
                this.isSaved = true;
                this.currentFileHandle = handle; // Set the new handle as current
                this.fileHandles[fileName] = handle; // Track it

                // Add to recent files
                if (this.rootStore.settingsStore) {
                     const file = {
                        name: fileName,
                         // Determine path based on whether it was saved into a known directory
                         directoryPath: this.directoryHandle && handle.isSameEntry // isSameEntry check might be needed
                            ? this.directoryHandle.name
                            : undefined,
                         relativePath: this.directoryHandle && handle.isSameEntry
                            ? fileName
                            : undefined,
                        lastOpened: new Date().toISOString(),
                         // Get size/modified from handle if possible
                    };
                    this.rootStore.settingsStore.addRecentFile(file);
                }
            });

            // Refresh directory tree if saved within the currently open directory
            if (this.directoryHandle) {
                await this.refreshDirectoryTree();
            }

            success = true;

        } catch (error) {
            console.error('Error saving workbook As:', error);
            saveError = error;
            success = false;
        }

         // Return success status, the actual fileName, the handle, and any error message
         return { success, fileName: success ? fileName : null, handle: success ? handle : null, error: saveError?.message };
    };

    saveWorkbookAsCSV = async (sheet, fileName) => {
        try {
            // Check if sheet is empty or undefined
            if (!sheet) {
                console.warn('No sheet provided for CSV export');
                return false;
            }

            // Get formatted file name from workbook
            const workbook = sheet.workbook;
            const suggestedName = workbook.isNewWorkbook
                ? workbook.properties.title
                : (workbook.originalFileName || fileName).replace(/\.(xlsx|csv)$/, '');

            let handle;
            let actualFileName = `${suggestedName}.csv`; // Initial guess

            // If we have a directory handle, try to save in that directory (optional, could always prompt)
            // Let's make CSV Save As always prompt for consistency with XLSX Save As
            // if (this.directoryHandle) {
            //     try {
            //         handle = await this.directoryHandle.getFileHandle(`${suggestedName}.csv`, { create: true });
            //         actualFileName = handle.name;
            //     } catch (error) {
            //         console.warn('Could not save CSV in directory, falling back to file picker:', error);
            //         handle = null;
            //     }
            // }

            // Always use the file picker for Save As CSV
            // if (!handle) {
                try {
                    handle = await window.showSaveFilePicker({
                        suggestedName: `${suggestedName}.csv`,
                        types: [{
                            description: 'CSV Files',
                            accept: {
                                'text/csv': ['.csv']
                            }
                        }]
                    });
                    actualFileName = handle.name; // Update with user's choice
                } catch (pickerError) {
                     if (pickerError.name === 'AbortError') {
                        console.log('User cancelled the CSV Save As dialog.');
                        return false; // Indicate failure/cancellation
                     }
                    throw pickerError;
                }
            // }

            const hasPermission = await this.verifyPermission(handle, true);
            if (!hasPermission) {
                throw new Error('Write permission denied');
            }

            let csvData;
            try {
                // Handle empty sheets
                if (sheet.rowCount === 0) {
                    csvData = '';  // Empty CSV for empty sheet
                } else {
                    // Use ExcelJS built-in CSV writer
                    const csvBuffer = await workbook.csv.writeBuffer({ sheetId: sheet.id });
                    // Need to decode buffer (assuming UTF-8)
                    const decoder = new TextDecoder('utf-8');
                    csvData = decoder.decode(csvBuffer);
                    // Old way: csvData = await sheet.csv.write(); // This might not exist or work as expected
                }
            } catch (error) {
                console.warn('Error generating CSV, falling back to empty CSV:', error);
                csvData = '';
            }

            const writable = await handle.createWritable();
            await writable.write(csvData);
            await writable.close();

            // CSV export is often treated as an export, not a primary save.
            // Do NOT update `isSaved` or `currentFileHandle` for the main workbook.
            // runInAction(() => {
            //     this.isSaved = true; // Maybe don't mark the *workbook* as saved
            // });

            // If we have a directory handle, rescan to update the file list
            if (this.directoryHandle) {
                await this.refreshDirectoryTree(); // Refresh to show the new CSV
            }

            return true; // Indicate success
        } catch (error) {
            console.error('Error saving CSV:', error);
            return false; // Indicate failure
        }
    };

    setCurrentFileHandle = action((handle) => {
        this.currentFileHandle = handle;
    });

    // Method to open a file from a handle or path
    openFileFromHandle = async (fileInfo) => {
        try {
            // If we have a valid handle, use it directly
            if (fileInfo.handle && typeof fileInfo.handle.getFile === 'function') {
                try {
                    const hasPermission = await this.verifyPermission(fileInfo.handle, false);
                    if (!hasPermission) {
                        throw new Error('Read permission denied');
                    }
                    
                    const file = await fileInfo.handle.getFile();
                    
                    // Update last opened timestamp
                    if (this.rootStore.settingsStore) {
                        this.rootStore.settingsStore.updateRecentFileTimestamp(fileInfo.path);
                    }
                    
                    return { file, handle: fileInfo.handle };
                } catch (error) {
                    console.warn('Error opening file from handle, trying directory lookup:', error);
                }
            }
            
            // If we have a directory handle and a file name, try to get the file from the directory
            if (this.directoryHandle && fileInfo.name) {
                try {
                    const fileHandle = await this.directoryHandle.getFileHandle(fileInfo.name);
                    const hasPermission = await this.verifyPermission(fileHandle, false);
                    if (!hasPermission) {
                        throw new Error('Read permission denied for file in directory');
                    }
                    
                    const file = await fileHandle.getFile();
                    
                    // Update last opened timestamp
                    if (this.rootStore.settingsStore) {
                        this.rootStore.settingsStore.updateRecentFileTimestamp(fileInfo.path);
                    }
                    
                    return { file, handle: fileHandle };
                } catch (error) {
                    console.warn('Error opening file from directory, falling back to file picker:', error);
                }
            }
            
            // If all else fails, prompt the user to select the file
            console.log('No valid file handle available, prompting user to select file');
            return await this.openFile();
        } catch (error) {
            console.error('Error opening file from handle:', error);
            throw error;
        }
    };

    // Show a more informative message when requesting directory access for a recent file
    requestDirectoryAccessForRecentFile = async (directoryPath) => {
        if (!this.isFileSystemAccessSupported()) {
            console.warn('File System Access API not supported');
            return null;
        }

        try {
            // Show a message to the user explaining why we need directory access again
            console.log(`Requesting access to directory: ${directoryPath}`);
            // We could show a toast or modal here explaining the situation
            
            const dirHandle = await window.showDirectoryPicker({
                mode: 'readwrite',
                // We can't set an initial directory, but we can guide the user
                id: 'recent-file-directory'
            });
            
            runInAction(() => {
                this.directoryHandle = dirHandle;
                this.currentPath = [dirHandle.name];
                localStorage.setItem('workbookDirectoryPath', dirHandle.name);
                
                // Add to accessible directories
                if (this.rootStore.settingsStore) {
                    this.rootStore.settingsStore.addAccessibleDirectory(dirHandle.name);
                }
            });
            
            return dirHandle;
        } catch (error) {
            console.error('Error requesting directory access:', error);
            return null;
        }
    };

    // Update the openRecentFile method to use this
    openRecentFile = async (fileInfo) => {
        // Clear any existing selection before attempting to open recent
        this.clearSelectedFile();
        try {
            // If we don't have directory info, fall back to file picker
            if (!fileInfo.directoryPath || !fileInfo.relativePath) {
                console.log('No directory info for recent file, prompting user to select file');
                // Clear selection since we're falling back
                this.clearSelectedFile();
                return await this.openFile();
            }
            
            // Check if we have access to the directory
            if (!this.directoryHandle || this.directoryHandle.name !== fileInfo.directoryPath) {
                // We need to request access to the directory first
                console.log('Need to request access to directory:', fileInfo.directoryPath);
                
                try {
                    // Use the new method with better messaging
                    await this.requestDirectoryAccessForRecentFile(fileInfo.directoryPath);
                    
                    // Check if we got access to the correct directory
                    if (!this.directoryHandle || this.directoryHandle.name !== fileInfo.directoryPath) {
                        console.log('User selected a different directory, prompting for file');
                        return await this.openFile();
                    }
                } catch (error) {
                    console.error('Error requesting directory access:', error);
                    return await this.openFile();
                }
            }
            
            // Now we have access to the directory, try to navigate to the file
            const pathParts = fileInfo.relativePath.split('/');
            let currentHandle = this.directoryHandle;
            
            // Navigate through subdirectories if needed
            for (let i = 0; i < pathParts.length - 1; i++) {
                try {
                    currentHandle = await currentHandle.getDirectoryHandle(pathParts[i]);
                } catch (error) {
                    console.error('Error navigating to subdirectory:', error);
                    return await this.openFile();
                }
            }
            
            // Get the file handle
            try {
                const fileName = pathParts[pathParts.length - 1];
                const fileHandle = await currentHandle.getFileHandle(fileName);
                const file = await fileHandle.getFile();
                
                // Update last opened timestamp
                this.rootStore.settingsStore.updateRecentFileTimestamp(fileInfo);
                
                // If the opened file is within the *current* directory view, select it.
                const filePathInCurrentDir = [this.directoryHandle?.name, ...fileInfo.relativePath.split('/')].join('/');
                if (this.directoryHandle?.name === fileInfo.directoryPath) {
                    runInAction(() => {
                        this.selectedFilePath = filePathInCurrentDir;
                         console.log("Selected file path set from recent file:", this.selectedFilePath);
                    });
                }

                return { file, handle: fileHandle };
            } catch (error) {
                console.error('Error opening file from directory:', error);
                 // Clear selection if failed
                 this.clearSelectedFile();
                return await this.openFile();
            }
        } catch (error) {
            console.error('Error opening recent file:', error);
             // Clear selection if failed
             this.clearSelectedFile();
            return await this.openFile();
        }
    };

    // Update the buildDirectoryTree method to ensure children are properly loaded and observed
    buildDirectoryTree = async (handle = this.directoryHandle, parentPath = []) => {
        if (!handle) return null;
        
        console.log('Building directory tree for:', handle.name);
        
        // Path for this node
        const nodePath = [...parentPath, handle.name];
        const pathKey = nodePath.join('/');
        const isRootNode = parentPath.length === 0;
        
        // Root is always expanded, otherwise check expandedPaths
        const wasExpanded = isRootNode || this.expandedPaths.has(pathKey);
        
        // Create the node structure
        const node = {
            kind: 'directory',
            name: handle.name,
            path: nodePath,
            isExpanded: wasExpanded, // Preserve expanded state
            isLoading: false,
            children: [], // Start with empty array
            handle,
            isRoot: isRootNode
        };
        
        // Update the directoryTree with this new structure
        runInAction(() => {
            if (isRootNode) {
                this.directoryTree = node;
                this.recentlyAccessedDirectory = handle.name;
                
                // Ensure root is always in the expanded set
                this.expandedPaths.add(pathKey);
            }
        });
        
        console.log('Directory node created for:', handle.name, 'expanded:', wasExpanded);
        
        // Always load children for all nodes (we'll need them if expanded or to check for expanded children)
        try {
            // Mark as loading
            runInAction(() => {
                node.isLoading = true;
            });
            
            // Load children
            const children = [];
            for await (const [name, childHandle] of handle.entries()) {
                // Skip hidden files/folders
                if (name.startsWith('.')) continue;
                
                const childPath = [...nodePath, name];
                const childPathKey = childPath.join('/');
                
                if (childHandle.kind === 'directory') {
                    // First create a placeholder
                    const dirNode = {
                        kind: 'directory',
                        name,
                        path: childPath,
                        isExpanded: this.expandedPaths.has(childPathKey),
                        isLoading: false,
                        children: [],
                        handle: childHandle
                    };
                    
                    children.push(dirNode);
                    
                    // If this child directory is expanded or has expanded children,
                    // recursively build its subtree
                    if (dirNode.isExpanded || this.hasExpandedChildren(childPathKey)) {
                        // Recursively build this directory subtree
                        const builtDirNode = await this.buildDirectoryTree(childHandle, nodePath);
                        
                        // Update with fully built node (if successful)
                        if (builtDirNode) {
                            // Find the index and replace with complete node
                            const index = children.findIndex(c => 
                                c.kind === 'directory' && c.name === name);
                            if (index !== -1) {
                                children[index] = builtDirNode;
                            }
                        }
                    }
                } else if (childHandle.kind === 'file') {
                    // Only include Excel and CSV files
                    if (name.endsWith('.xlsx') || name.endsWith('.csv')) {
                        children.push({
                            kind: 'file',
                            name,
                            path: childPath,
                            handle: childHandle
                        });
                    }
                }
            }
            
            // Sort: directories first, then files, both alphabetically
            children.sort((a, b) => {
                if (a.kind !== b.kind) {
                    return a.kind === 'directory' ? -1 : 1;
                }
                return a.name.localeCompare(b.name);
            });
            
            // Update the node with the loaded children
            runInAction(() => {
                node.children = children;
                node.isLoading = false;
            });
            
            // If this is the root node, update the directoryTree with the complete structure
            if (isRootNode) {
                runInAction(() => {
                    // Copy to trigger MobX reactivity
                    this.directoryTree = {...node};
                    console.log(`Finished building tree with ${children.length} root children`);
                });
            }
        } catch (error) {
            console.error('Error loading directory children:', error);
            runInAction(() => {
                node.isLoading = false;
                node.error = `Error: ${error.message}`;
            });
        }
        
        return node;
    };

    // Helper to check if a path has any expanded child paths
    hasExpandedChildren = (parentPath) => {
        for (const path of this.expandedPaths) {
            // Check if this path is a child of parentPath (starts with parentPath/)
            if (path.startsWith(parentPath + '/')) {
                return true;
            }
        }
        return false;
    };

    // Update refreshDirectoryTree to better handle expanded folders
    refreshDirectoryTree = async () => {
        if (!this.directoryHandle) return;

        console.log('Refreshing directory tree while preserving expanded state');
        // Log state *before* remembering
        console.log('Current selectedFilePath BEFORE refresh:', this.selectedFilePath);
        console.log('Currently expanded paths:', Array.from(this.expandedPaths));

        // Remember the selected file path string
        const currentSelectedPath = this.selectedFilePath;

        // Reset the tree but keep the expanded paths set
        await this.buildDirectoryTree(this.directoryHandle); // Rebuilds this.directoryTree

        // Restore selected file string if it still exists
        if (currentSelectedPath) {
            // We just restore the string. The DirectoryNode component will check
            // if its path matches this string to determine if it's selected.
            runInAction(() => {
                this.selectedFilePath = currentSelectedPath;
                console.log("Restored selected file path string AFTER refresh:", this.selectedFilePath);
            });
        } else {
            console.log("No selected file path to restore after refresh.");
        }
    };

    // Add method to attempt auto-restoration of most recent directory
    attemptAutoRestore = async () => {
        if (this.recentDirectories.length === 0) return false;
        
        const mostRecent = this.recentDirectories[0];
        console.log('Attempting to auto-restore directory:', mostRecent.name);
        
        try {
            const result = await this.tryRestoreDirectoryAccess(mostRecent.handle);
            
            // If successful, make sure we have a proper recentlyAccessedDirectory for the UI
            if (result) {
                runInAction(() => {
                    this.recentlyAccessedDirectory = mostRecent.name;
                });
                
                console.log('Auto-restore successful, directory tree built with:', this.directoryTree.name);
            }
            
            return result;
        } catch (error) {
            console.error('Error auto-restoring directory access:', error);
            return false;
        }
    };

    // Replace toggleExpandNode with a simpler version that doesn't use fetchNodeChildren
    toggleExpandNode = (node) => {
        if (!node || node.kind !== 'directory') return;
        
        // Create a path string to use as the key
        const pathKey = node.path.join('/');
        
        // Toggle expansion state
        runInAction(() => {
            if (node.isExpanded) {
                // Closing folder
                node.isExpanded = false;
                this.expandedPaths.delete(pathKey);
            } else {
                // Opening folder
                node.isExpanded = true;
                this.expandedPaths.add(pathKey);
                
                // If no children loaded and not loading, set loading flag and trigger a refresh
                if (node.children.length === 0 && !node.isLoading) {
                    node.isLoading = true;
                    
                    // Use a direct implementation instead of relying on fetchNodeChildren
                    this.loadNodeChildren(node).catch(error => {
                        console.error("Error loading folder contents:", error);
                        runInAction(() => {
                            node.isLoading = false;
                            node.error = "Failed to load contents";
                        });
                    });
                }
            }
        });
    };

    // Add a simple implementation that doesn't rely on fetchNodeChildren
    loadNodeChildren = async (node) => {
        if (!node || !node.handle || node.kind !== 'directory') return;
        
        try {
            // Load children directly
            const children = [];
            for await (const [name, childHandle] of node.handle.entries()) {
                // Skip hidden files/folders
                if (name.startsWith('.')) continue;
                
                const childPath = [...node.path, name];
                const childPathKey = childPath.join('/');
                
                if (childHandle.kind === 'directory') {
                    children.push({
                        kind: 'directory',
                        name,
                        path: childPath,
                        isExpanded: this.expandedPaths.has(childPathKey),
                        isLoading: false,
                        children: [],
                        handle: childHandle
                    });
                } else if (childHandle.kind === 'file') {
                    // Only include Excel and CSV files
                    if (name.endsWith('.xlsx') || name.endsWith('.csv')) {
                        children.push({
                            kind: 'file',
                            name,
                            path: childPath,
                            handle: childHandle
                        });
                    }
                }
            }
            
            // Sort: directories first, then files, both alphabetically
            children.sort((a, b) => {
                if (a.kind !== b.kind) {
                    return a.kind === 'directory' ? -1 : 1;
                }
                return a.name.localeCompare(b.name);
            });
            
            // Update the node with the loaded children
            runInAction(() => {
                node.children = children;
                node.isLoading = false;
            });
        } catch (error) {
            console.error('Error loading directory children:', error);
            runInAction(() => {
                node.isLoading = false;
                node.error = `Error: ${error.message}`;
            });
        }
    };
}